import type { RTPBucketRow, RTPDownloadResult } from '@/types/rtpDownload';

export const RTP_CSV_HEADERS = [
  'DateTime',
  'Side',
  'Stride Count',
  'Total Duration [s]',
  'Mean Stride Duration [s]',
  'Total Distance [m]',
  'Mean Stride Length [m]',
];

function formatFallbackBucketLabel(timestamp: number, grouping: RTPDownloadResult['grouping']): string {
  const date = new Date(timestamp * 1000);

  switch (grouping) {
    case 'hour':
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    case 'day':
      return date.toISOString().split('T')[0]!;
    case 'week': {
      const weekStart = new Date(date);
      const dayOfWeek = weekStart.getDay();
      const daysSinceMonday = (dayOfWeek + 6) % 7;
      weekStart.setDate(weekStart.getDate() - daysSinceMonday);
      return weekStart.toISOString().split('T')[0]!;
    }
    case 'month': {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    }
  }
}

interface FootBucket {
  count: number;
  durationAvg: number | null;
  lengthAvg: number | null;
}

function getNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function extractFootBuckets(points: RTPBucketRow[]): Map<number, FootBucket> {
  const buckets = new Map<number, FootBucket>();

  points.forEach((point) => {
    const timestamp = typeof point.timestamp === 'number' ? point.timestamp : null;
    if (timestamp === null) return;

    const count = getNumber(point.count) ?? 0;
    const durationAvg = getNumber(point['duration-avg']);
    const lengthAvg = getNumber(point['length-avg']);

    buckets.set(timestamp, {
      count,
      durationAvg,
      lengthAvg,
    });
  });

  return buckets;
}

function formatNumber(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '';
  return String(value);
}

export function buildRTPCsvRows(result: RTPDownloadResult): string[][] {
  const leftBuckets = extractFootBuckets(result.leftFoot);
  const rightBuckets = extractFootBuckets(result.rightFoot);
  const rows: string[][] = [];

  const timestamps = new Set<number>([
    ...leftBuckets.keys(),
    ...rightBuckets.keys(),
  ]);

  const sortedTimestamps = Array.from(timestamps).sort((a, b) => a - b);

  sortedTimestamps.forEach((timestamp) => {
    const left = leftBuckets.get(timestamp);
    const right = rightBuckets.get(timestamp);
    const leftPoint = result.leftFoot.find((point) => point.timestamp === timestamp);
    const rightPoint = result.rightFoot.find((point) => point.timestamp === timestamp);
    const bucketLabelFromApi =
      (typeof leftPoint?.bucketLabel === 'string' ? leftPoint.bucketLabel : null) ||
      (typeof rightPoint?.bucketLabel === 'string' ? rightPoint.bucketLabel : null);
    const dateTime = bucketLabelFromApi || formatFallbackBucketLabel(timestamp, result.grouping);

    if (left) {
      const totalDuration =
        left.durationAvg === null ? null : left.durationAvg * left.count;
      const totalDistance =
        left.lengthAvg === null ? null : left.lengthAvg * left.count;

      rows.push([
        dateTime,
        'Left',
        formatNumber(left.count),
        formatNumber(totalDuration),
        formatNumber(left.durationAvg),
        formatNumber(totalDistance),
        formatNumber(left.lengthAvg),
      ]);
    }

    if (right) {
      const totalDuration =
        right.durationAvg === null ? null : right.durationAvg * right.count;
      const totalDistance =
        right.lengthAvg === null ? null : right.lengthAvg * right.count;

      rows.push([
        dateTime,
        'Right',
        formatNumber(right.count),
        formatNumber(totalDuration),
        formatNumber(right.durationAvg),
        formatNumber(totalDistance),
        formatNumber(right.lengthAvg),
      ]);
    }
  });

  return rows;
}

export function buildRTPCsvString(headers: string[], rows: string[][]): string {
  const escapeCSV = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const headerRow = headers.map(escapeCSV).join(',');
  const dataRows = rows.map((row) => row.map(escapeCSV).join(','));
  return [headerRow, ...dataRows].join('\n');
}
